import { primeVueConfig } from "@eg_ui/src/utils/primevue-theme";
import { createVueApp } from "@eg_ui/src/utils/vue";
import PrimeVue from "primevue/config";
import { defineAsyncComponent } from "vue";

import { sharedBase } from "./base";

import "@eg_ui/src/utils/postDomReady";

import FlashMessages from "@eg_ui/src/components/flashes/client/FlashMessages/FlashMessages.vue";
import DesktopSignInMenu from "@eg_ui/src/components/globals/DesktopSignInMenu/DesktopSignInMenu.vue";
import DesktopUserInfoMenu from "@eg_ui/src/components/globals/DesktopUserInfoMenu/DesktopUserInfoMenu.vue";
import PhotoSwipeTemplate from "@eg_ui/src/components/globals/PhotoSwipeTemplate/PhotoSwipeTemplate.vue";
import UserAvatar from "@eg_ui/src/components/globals/UserAvatar/UserAvatar.vue";
import SkipButton from "@eg_ui/src/components/globals/_buttons/SkipButton/SkipButton.vue";
import LeftColumnNav from "@eg_ui/src/components/globals/_layouts/LeftColumnNav/LeftColumnNav.vue";
import ThreeColumnLayout from "@eg_ui/src/components/globals/_layouts/ThreeColumnLayout/ThreeColumnLayout.vue";
import SiteNavigation from "@eg_ui/src/components/navigation/SiteNavigation/SiteNavigation.vue";

import "@skyline2/vue/dist/skyline.css";

const allComponents = { ...sharedBase["components"] };
Object.assign(allComponents, {
    BannerView: defineAsyncComponent(() => import("@eg_ui/src/components/banners/BannerView/BannerView.vue")),
    CompleteProfileNotice: defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/CompleteProfileNotice/CompleteProfileNotice.vue"),
    ),
    DesktopSignInMenu,
    DesktopUserInfoMenu,
    FlashMessages,
    LeftColumnNav,
    PhotoSwipeTemplate,
    SiteNavigation,
    SkipButton,
    ThreeColumnLayout,
    UserAvatar,
    "allow-cookies-prompt": defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/AllowCookiesPrompt/AllowCookiesPrompt.vue"),
    ),
    "anonymous-account-app": defineAsyncComponent(
        () => import("@eg_ui/src/components/authentication/_account/AnonymousAccountApp/AnonymousAccountApp.vue"),
    ),
    "authenticated-account-app": defineAsyncComponent(
        () =>
            import("@eg_ui/src/components/authentication/_account/AuthenticatedAccountApp/AuthenticatedAccountApp.vue"),
    ),
    "back-to-top-button": defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/_buttons/BackToTopButton/BackToTopButton.vue"),
    ),
    "erg-banner": defineAsyncComponent(() => import("@eg_ui/src/components/globals/ErgBanner/ErgBanner.vue")),
    "erg-right-sidebar-tile": defineAsyncComponent(
        () => import("@eg_ui/src/components/whatsUp/_tiles/ErgRightSidebarTile/ErgRightSidebarTile.vue"),
    ),
    "erg-welcome": defineAsyncComponent(() => import("@eg_ui/src/components/globals/ErgWelcome/ErgWelcome.vue")),
    "modify-feed-score-button": defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/_buttons/ModifyFeedScoreButton/ModifyFeedScoreButton.vue"),
    ),
    "save-content-button": defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/_buttons/SaveContentButton/SaveContentButton.vue"),
    ),
    "third-party-video-player": defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/ThirdPartyVideoPlayer/ThirdPartyVideoPlayer.vue"),
    ),
    "user-alias": defineAsyncComponent(() => import("@eg_ui/src/components/globals/UserAlias/UserAlias.vue")),
    "user-groups-label": defineAsyncComponent(
        () => import("@eg_ui/src/components/globals/UserGroupsLabel/UserGroupsLabel.vue"),
    ),
});

const allMethods = { ...sharedBase["methods"] };
Object.assign(allMethods, {
    onMount() {
        //@ts-expect-error this does exist
        if (window.CKEDITOR) {
            //@ts-expect-error this does exist
            window.CKEDITOR.disableAutoInline = true;
        }

        const getVh = () => {
            // Get the actual vh because vh units in CSS are not calculated in the same way by iOS/Android
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        };

        getVh();

        window.addEventListener("resize", () => {
            getVh();
        });

        // Workaround to ensure that SkyPopoverMenu does not create an app for each instance of a dropdown in the page.
        // #sky-popover-menu-target cannot just be added as an element within the Vue-controlled region of the page
        // because this causes all SkyPopoverMenus that exist when mounted() is run to lose their SkyDropdownMenus.
        document.getElementById("app-container")!.append(document.getElementById("sky-popover-menu-target")!);
    },
});

const app = await createVueApp({
    ...sharedBase,
    components: allComponents,
    methods: allMethods,
});

app.use(PrimeVue, primeVueConfig);

export { app };
